import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { CustomPackage, CustomPackagePayload } from '../types/custom-packages';

type Wrapped<T> = { data: T };

/** Returns `custom:{id}` keys' custom-ness without knowing the id. */
export function isCustomPackageKey(key: string): boolean {
  return key.startsWith('custom:');
}

export function useCustomPackages() {
  const api = useApiClient();
  return useQuery<CustomPackage[], Error>({
    queryKey: queryKeys.customPackages.list(),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<CustomPackage[]>>('/admin/custom-packages', signal);
      return response.data;
    },
  });
}

export function useCreateCustomPackage() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CustomPackage, Error, CustomPackagePayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<CustomPackage>>('/admin/custom-packages', payload);
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customPackages.all });
    },
  });
}

export function useUpdateCustomPackage(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CustomPackage, Error, CustomPackagePayload>({
    mutationFn: async (payload) => {
      const response = await api.put<Wrapped<CustomPackage>>(
        `/admin/custom-packages/${id}`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customPackages.all });
    },
  });
}

export function useDeleteCustomPackage() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await api.del(`/admin/custom-packages/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customPackages.all });
      // Deleting the package the default-package setting pointed at clears
      // the setting backend-side - refresh the cached /admin/me payload.
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}
